// src/pages/Dashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const [usage, setUsage] = useState(null);
  const [hovered, setHovered] = useState(false);

  // ---- Auth flow ----
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");
    const userId = urlParams.get("user_id");
    const flag = urlParams.get("flag");

    if (token && userId) {
      localStorage.setItem("jwt_token", token);
      localStorage.setItem("user_id", userId);
      localStorage.setItem(
        "passwordChangeRequired",
        flag === "1" ? "true" : "false"
      );
      window.history.replaceState({}, document.title, "/dashboard");
    } else {
      const existingToken = localStorage.getItem("jwt_token");
      if (!existingToken) {
        navigate("/login");
      }
    }
  }, [location.search, navigate]);

  // ---- Fetch usage ----
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const userId = localStorage.getItem("user_id");
        if (!userId) return;

        const res = await fetch(`${BACKEND_URL}/api/payments/usage/${userId}`);
        if (!res.ok) throw new Error("Failed to fetch usage");

        const data = await res.json();
        setUsage(data);
      } catch (err) {
        console.error("⚠️ Error fetching usage:", err);
      }
    };

    fetchUsage();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("passwordChangeRequired");
    navigate("/login");
  };

  const isLimitReached = usage && usage.usage_percent >= 100;

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-900 flex overflow-hidden">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 px-6 py-8 flex flex-col justify-between sticky top-0 h-screen shadow-xl">
        <div>
          <div className="text-2xl font-black tracking-wider text-[#8ff0b3] mb-8">
            Nokose
          </div>

          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="w-full text-left px-4 py-3 rounded-lg border border-indigo-700 bg-indigo-700 text-white font-semibold cursor-pointer transition hover:bg-indigo-800 hover:border-indigo-800 mb-2"
          >
            Profile
          </button>

          {/* Usage bar */}
          {usage && (
            <div className="my-6 px-4 py-4 rounded-lg bg-white border border-slate-300 shadow-sm">
              <p className="text-sm font-semibold text-slate-800 mb-3">
                {usage.usage_percent}% of {usage.plan_name} plan used
              </p>
              <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all"
                  style={{ width: `${usage.usage_percent}%` }}
                />
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate("/payment")}
            className="w-full px-4 py-3 rounded-lg border border-blue-700 bg-blue-700 text-white font-semibold cursor-pointer transition hover:bg-blue-800 hover:border-blue-800 mt-4"
          >
            Upgrade Plan
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 lg:p-12 overflow-auto">
        {/* Top Header Bar */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-black text-slate-900">Dashboard</h1>
            <p className="mt-2 text-slate-600 text-base">
              Manage your experiments and profile from a single place.
            </p>

            <div className="flex gap-4 mt-6">
              {/* Create Experiment Button with Tooltip */}
              <div
                className="relative inline-block"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (!isLimitReached) {
                      navigate("/createexperiment");
                    }
                  }}
                  className={`px-6 py-3 rounded-lg font-bold text-base transition ${
                    isLimitReached
                      ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 cursor-pointer"
                  }`}
                  disabled={isLimitReached}
                >
                  Create Experiment
                </button>

                {/* Tooltip appears only on hover */}
                {isLimitReached && hovered && (
                  <div className="absolute top-[120%] left-1/2 transform -translate-x-1/2 bg-slate-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap z-10 border border-white/10 shadow-lg">
                    Limit reached. Upgrade plan to create new experiments.
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => navigate("/viewexperiment")}
                className="px-6 py-3 rounded-lg border border-indigo-700 bg-indigo-700 text-white font-bold text-base cursor-pointer transition hover:bg-indigo-800 hover:border-indigo-800"
              >
                View Experiments
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="px-5 py-3 rounded-lg border border-red-300 bg-white text-red-700 font-semibold cursor-pointer transition hover:bg-red-50 hover:border-red-400"
          >
            Logout
          </button>
        </div>

        {/* Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-12">
          {/* Quick Actions Card */}
          <div className="lg:col-span-1 rounded-2xl border border-white/10 bg-[#173a32] p-6 shadow-xl hover:border-emerald-400/30 transition">
            <p className="text-sm font-semibold text-emerald-200 uppercase tracking-wider">⚡ Quick Actions</p>
            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => navigate("/viewexperiment")}
                className="w-full text-left px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 hover:border-emerald-400/30 transition"
              >
                📊 View All Experiments
              </button>
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="w-full text-left px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 hover:border-emerald-400/30 transition"
              >
                👤 Account Settings
              </button>
              <button
                type="button"
                onClick={() => navigate("/payment")}
                className="w-full text-left px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 hover:border-emerald-400/30 transition"
              >
                💳 View Plans
              </button>
            </div>
          </div>

          {/* Getting Started */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#173a32] p-6 shadow-xl">
            <p className="text-sm font-semibold text-emerald-200 uppercase tracking-wider">🚀 Getting Started</p>
            <div className="mt-6 space-y-4">
              <div className="flex gap-3 items-start">
                <span className="text-xl flex-shrink-0">1️⃣</span>
                <div>
                  <p className="font-semibold text-white text-sm">Create Your First Experiment</p>
                  <p className="text-xs text-slate-400 mt-1">Design A/B tests without code!</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <span className="text-xl flex-shrink-0">2️⃣</span>
                <div>
                  <p className="font-semibold text-white text-sm">Launch & Measure</p>
                  <p className="text-xs text-slate-400 mt-1">See real-time results as visitors interact</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <span className="text-xl flex-shrink-0">3️⃣</span>
                <div>
                  <p className="font-semibold text-white text-sm">Optimize & Scale</p>
                  <p className="text-xs text-slate-400 mt-1">Use data to drive growth</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
